import { PostgrestSingleResponse, SupabaseClient } from '@supabase/supabase-js';

import {
  CreateMediaManagerBoard,
  CreateMediaManagerTemplate,
  MediaManagerTemplateFilter,
  UpdateMediaManagerBoard,
  UpdateMediaManagerTemplate,
} from '../../domain/interfaces/supabase/media-manager/actions';
import { MediaManagerBoardDto, MediaManagerTemplateDto } from '../../domain/interfaces/supabase/media-manager/dto';
import { MediaManagerBoard, MediaManagerTemplate } from '../../domain/interfaces/supabase/media-manager/entity';
import mediaManagerBoardDataMapper from '../../domain/mappers/media-manager-board-data-mapper';
import mediaManagerTemplateDataMapper from '../../domain/mappers/media-manager-template-data-mapper';

const TEMPLATES_TABLE = 'media_manager_templates';
const BOARDS_TABLE = 'media_manager_boards';

export default class MediaManager {
  constructor(private supabase: SupabaseClient) {}

  async getTemplates(filter?: MediaManagerTemplateFilter): Promise<MediaManagerTemplate[]> {
    let query = this.supabase.from(TEMPLATES_TABLE).select('*').order('created_at', { ascending: false });

    if (filter?.id !== undefined) {
      query = query.eq('id', filter.id);
    }
    if (filter?.tournamentId !== undefined) {
      query = query.eq('tournament_id', filter.tournamentId);
    }
    if (filter?.sportType) {
      query = query.eq('sport_type', filter.sportType);
    }

    const { data, error } = (await query) as PostgrestSingleResponse<MediaManagerTemplateDto[]>;

    if (error) {
      console.error(error);
      throw error;
    }

    return data.map(mediaManagerTemplateDataMapper);
  }

  async getTemplateById(id: number): Promise<MediaManagerTemplate> {
    const { data, error } = (await this.supabase
      .from(TEMPLATES_TABLE)
      .select('*')
      .eq('id', id)
      .single()) as PostgrestSingleResponse<MediaManagerTemplateDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerTemplateDataMapper(data);
  }

  async createTemplate(props: CreateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const { data, error } = (await this.supabase
      .from(TEMPLATES_TABLE)
      .insert({
        name: props.name,
        tournament_id: props.tournamentId,
        sport_type: props.sportType ?? null,
      })
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerTemplateDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerTemplateDataMapper(data);
  }

  async updateTemplate(props: UpdateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const { id, name, sportType } = props;

    if (name === undefined && sportType === undefined) {
      throw new Error('Nothing to update');
    }

    const updates: Partial<MediaManagerTemplateDto> = {};
    if (name !== undefined) updates.name = name;
    if (sportType !== undefined) updates.sport_type = sportType;

    const { data, error } = (await this.supabase
      .from(TEMPLATES_TABLE)
      .update(updates)
      .eq('id', id)
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerTemplateDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerTemplateDataMapper(data);
  }

  async deleteTemplate(id: number): Promise<void> {
    const { error } = await this.supabase.from(TEMPLATES_TABLE).delete().eq('id', id);

    if (error) {
      console.error(error);
      throw error;
    }
  }

  async getBoardById(id: number): Promise<MediaManagerBoard> {
    const { data, error } = (await this.supabase
      .from(BOARDS_TABLE)
      .select('*')
      .eq('id', id)
      .single()) as PostgrestSingleResponse<MediaManagerBoardDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerBoardDataMapper(data);
  }

  async getBoardsByTemplateId(templateId: number): Promise<MediaManagerBoard[]> {
    const { data, error } = (await this.supabase
      .from(BOARDS_TABLE)
      .select('*')
      .eq('template_id', templateId)
      .order('order', { ascending: true })) as PostgrestSingleResponse<MediaManagerBoardDto[]>;

    if (error) {
      console.error(error);
      throw error;
    }

    return data.map(mediaManagerBoardDataMapper);
  }

  async createBoard(props: CreateMediaManagerBoard): Promise<MediaManagerBoard> {
    const { data, error } = (await this.supabase
      .from(BOARDS_TABLE)
      .insert({
        name: props.name,
        objects: props.objects,
        version: props.version,
        template_id: props.templateId,
        width: props.width,
        height: props.height,
        order: props.order,
      })
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerBoardDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerBoardDataMapper(data);
  }

  async updateBoard(props: UpdateMediaManagerBoard): Promise<MediaManagerBoard> {
    const { id, name, objects, version, templateId, width, height, order } = props;

    if (
      name === undefined &&
      objects === undefined &&
      version === undefined &&
      templateId === undefined &&
      width === undefined &&
      height === undefined &&
      order === undefined
    ) {
      throw new Error('Nothing to update');
    }

    const updates: Partial<MediaManagerBoardDto> = {};
    if (name !== undefined) updates.name = name;
    if (objects !== undefined) updates.objects = objects;
    if (version !== undefined) updates.version = version;
    if (templateId !== undefined) updates.template_id = templateId;
    if (width !== undefined) updates.width = width;
    if (height !== undefined) updates.height = height;
    if (order !== undefined) updates.order = order;

    const { data, error } = (await this.supabase
      .from(BOARDS_TABLE)
      .update(updates)
      .eq('id', id)
      .select()
      .single()) as PostgrestSingleResponse<MediaManagerBoardDto>;

    if (error) {
      console.error(error);
      throw error;
    }

    return mediaManagerBoardDataMapper(data);
  }

  async deleteBoard(id: number): Promise<void> {
    const { error } = await this.supabase.from(BOARDS_TABLE).delete().eq('id', id);

    if (error) {
      console.error(error);
      throw error;
    }
  }

  async getTemplateWithBoards(templateId: number): Promise<{ template: MediaManagerTemplate; boards: MediaManagerBoard[] }> {
    const [template, boards] = await Promise.all([
      this.getTemplateById(templateId),
      this.getBoardsByTemplateId(templateId),
    ]);
    return { template, boards };
  }
}
